import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import authService from "../services/authService";
import { fetchProfile, updateProfile } from "../../pages/AdminProfile/services/profileSlice";
import { clearSession, startSession } from "../../../Controllers/sessionStorage";

const safeParse = (value) => {
  try {
    if (!value || value === "undefined") return null;
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const sessionAdmin = safeParse(sessionStorage.getItem("adminInfo"));

export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async (loginData, thunkAPI) => {
    try {
      const res = await authService.login(loginData);

      if (res.status !== true) {
        return thunkAPI.rejectWithValue(res);
      }

      const admin = res.data;

      sessionStorage.setItem("isAuth", "true");
      sessionStorage.setItem("adminInfo", JSON.stringify(admin));
      sessionStorage.setItem("token", admin.token);
      startSession();

      return admin;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data);
    }
  }
);

export const logoutUser = createAsyncThunk(
  "auth/logoutUser",
  async (_, thunkAPI) => {
    try {
      await authService.logout();
      return true;
    } catch (error) {
      // Even if API call fails, we still want to clear local session
      return thunkAPI.rejectWithValue(error.response?.data);
    }
  }
);

const initialState = {
  admin: sessionAdmin || null,
  isAuth: sessionStorage.getItem("isAuth") === "true",
  isLoading: false,
  isError: false,
  message: "",
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: (state) => {
      state.admin = null;
      state.isAuth = false;
      state.isError = false;
      state.message = "";

      sessionStorage.removeItem("isAuth");
      sessionStorage.removeItem("adminInfo");
      sessionStorage.removeItem("token");
      clearSession();
    },
    clearAuthError: (state) => {
      state.isError = false;
      state.message = "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.message = "";
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuth = true;
        state.admin = action.payload;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload?.message || "Login failed";
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.admin = null;
        state.isAuth = false;
        state.isError = false;
        state.message = "";

        sessionStorage.removeItem("isAuth");
        sessionStorage.removeItem("adminInfo");
        sessionStorage.removeItem("token");
        clearSession();
      })
      .addCase(logoutUser.rejected, (state) => {
        // Clear session locally even if API call failed
        state.admin = null;
        state.isAuth = false;

        sessionStorage.removeItem("isAuth");
        sessionStorage.removeItem("adminInfo");
        sessionStorage.removeItem("token");
        clearSession();
      })
      .addCase(fetchProfile.fulfilled, (state, action) => {
        state.admin = { ...state.admin, ...action.payload };
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.admin = { ...state.admin, ...action.payload.data };
      });
  },
});

export const { logout, clearAuthError } = authSlice.actions;
export default authSlice.reducer;